import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface FieldDefinition {
  id: string;
  entityType: string;
  fieldName: string;
  displayName: string;
  fieldType: string;
  isRequired: boolean;
  isUnique: boolean;
  isSearchable: boolean;
  displayOrder: number;
}

const ENTITY_TYPES = [
  'ACCOUNT',
  'CONTACT',
  'OPPORTUNITY',
  'LEAD',
  'CAMPAIGN',
  'STAGE_TRANSITION',
];

const FIELD_TYPES = [
  'TEXT',
  'SEARCHABLE_TEXT',
  'INTEGER',
  'NUMERIC',
  'DATE',
  'BOOLEAN',
  'FILE_URL',
];

const TYPE_BADGES: Record<string, string> = {
  TEXT: 'bg-muted text-foreground',
  SEARCHABLE_TEXT: 'bg-blue-900/40 text-blue-300',
  INTEGER: 'bg-emerald-900/40 text-emerald-300',
  NUMERIC: 'bg-emerald-900/40 text-emerald-300',
  DATE: 'bg-amber-900/40 text-amber-300',
  BOOLEAN: 'bg-purple-900/40 text-purple-300',
  FILE_URL: 'bg-cyan-900/40 text-cyan-300',
};

export function CustomFieldsPage() {
  const [entityType, setEntityType] = useState('ACCOUNT');
  const [fields, setFields] = useState<FieldDefinition[]>([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<FieldDefinition[]>(
        '/custom-fields/definitions',
        { params: { entityType } },
      );
      setFields(data);
    } catch {
      setFields([]);
    }
  }, [entityType]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleDelete(id: string) {
    await apiClient.delete(`/custom-fields/definitions/${id}`);
    load();
  }

  return (
    <div className="space-y-4">
      <CreateFieldModal
        open={showCreate}
        entityType={entityType}
        onClose={() => setShowCreate(false)}
        onCreated={load}
      />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Custom Fields</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          + Field
        </Button>
      </div>

      <div className="flex gap-2">
        {ENTITY_TYPES.map((et) => (
          <Button
            key={et}
            size="sm"
            variant={entityType === et ? 'default' : 'outline'}
            onClick={() => setEntityType(et)}
          >
            {et}
          </Button>
        ))}
      </div>

      <div className="rounded-lg border bg-card">
        <ul className="divide-y">
          {fields.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between px-4 py-3"
            >
              <div className="flex items-center gap-3">
                <div>
                  <div className="font-medium">{f.displayName}</div>
                  <div className="text-xs text-muted-foreground">
                    {f.fieldName} · order {f.displayOrder}
                    {f.isRequired && ' · required'}
                    {f.isUnique && ' · unique'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    TYPE_BADGES[f.fieldType] ?? 'bg-muted text-foreground'
                  }`}
                >
                  {f.fieldType}
                </span>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(f.id)}
                >
                  Archive
                </Button>
              </div>
            </li>
          ))}
          {fields.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">
              No custom fields defined for {entityType}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function CreateFieldModal({
  open,
  entityType,
  onClose,
  onCreated,
}: {
  open: boolean;
  entityType: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [fieldName, setFieldName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [fieldType, setFieldType] = useState('TEXT');
  const [isRequired, setIsRequired] = useState(false);
  const [isUnique, setIsUnique] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!fieldName.trim() || !displayName.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post('/custom-fields/definitions', {
        entityType,
        fieldName: fieldName.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        displayName: displayName.trim(),
        fieldType,
        isRequired,
        isUnique,
        isSearchable: fieldType === 'SEARCHABLE_TEXT',
      });
      setFieldName('');
      setDisplayName('');
      setFieldType('TEXT');
      setIsRequired(false);
      setIsUnique(false);
      onCreated();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">
          Add Custom Field to {entityType}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Display Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={displayName}
              onChange={(e) => {
                setDisplayName(e.target.value);
                if (!fieldName) {
                  setFieldName(
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, '_')
                      .replace(/^_|_$/g, ''),
                  );
                }
              }}
              placeholder="Annual Revenue"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">
              Column Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={fieldName}
              onChange={(e) =>
                setFieldName(
                  e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''),
                )
              }
              placeholder="annual_revenue"
              required
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Type</label>
            <select
              value={fieldType}
              onChange={(e) => setFieldType(e.target.value)}
              className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
            >
              {FIELD_TYPES.map((ft) => (
                <option key={ft} value={ft}>
                  {ft}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
              />
              Required
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isUnique}
                onChange={(e) => setIsUnique(e.target.checked)}
              />
              Unique
            </label>
          </div>
          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Field'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
